import { type FC, useState, useEffect, useCallback, Suspense } from 'react';
import { trackEvent } from 'dibs-tracking';
import {
    usePreloadedQuery,
    useQueryLoader,
    type PreloadedQuery,
    fetchQuery,
    useRelayEnvironment,
} from 'react-relay';
import authModalLoader from 'dibs-buyer-layout/src/utils/AuthModalLoader';
import { ModalContainer } from 'dibs-elements/exports/ModalContainer';
import { ModalCloseButton } from 'dibs-elements/exports/ModalCloseButton';
import { WithWalletConnectionEnsured } from 'dibs-crypto-utils/exports/WithWalletConnectionEnsured';
import { getBuyerId } from 'dibs-cookie-jar';
import { addLoginHandlers } from 'dibs-buyer-layout/src/utils/loginHandlers';
import { useWeb3EagerConnect } from 'dibs-crypto-utils/exports/useWeb3EagerConnect';
import { useWeb3Connection } from 'dibs-crypto-utils/exports/useWeb3Connection';
import { usePersistedWalletAddress } from 'dibs-crypto-utils/exports/usePersistedWalletAddress';
import { ConnectWalletButton } from './ConnectWalletButton';
import { ConnectedWalletStatus } from './ConnectedWalletStatus';
import {
    ethereumWalletStatusQuery,
    type ethereumWalletStatusQueryType,
} from './ethereumWalletStatusQuery';

const EthereumWalletComponent: FC<{
    queryReference: PreloadedQuery<ethereumWalletStatusQueryType>;
    gaLabel?: string;
    refetchQuery: (userId: string) => void;
    isMobile: boolean;
}> = ({ queryReference, gaLabel, refetchQuery, isMobile }) => {
    const [hasActiveAccounts, setHasActiveAccounts] = useState(true);
    const { account, unsupportedChain } = useWeb3Connection();
    const triedEagerConnect = useWeb3EagerConnect();

    const { viewer } = usePreloadedQuery<ethereumWalletStatusQueryType>(
        ethereumWalletStatusQuery,
        queryReference
    );
    const { persistedAddress, userId } = usePersistedWalletAddress({ viewer });

    useEffect(() => {
        const { ethereum } = window;
        const handleAccountChange = (accounts: string[]): void => {
            setHasActiveAccounts(accounts.length !== 0);
        };
        if (ethereum && ethereum.on) {
            ethereum.on('accountsChanged', handleAccountChange);
        }
        addLoginHandlers(() => {
            const newId = getBuyerId(document.cookie);
            refetchQuery(newId);
        });
        return () => {
            if (ethereum && ethereum.removeListener) {
                ethereum.removeListener('accountsChanged', handleAccountChange);
            }
        };
    }, [refetchQuery]);

    // show if we have persisted address, and atleast one account is connected in MM
    const showAccountBalance =
        !!viewer.user &&
        Boolean(userId && hasActiveAccounts && persistedAddress && (account || unsupportedChain));

    const [isOpen, setIsOpen] = useState(false);
    const { connected } = useWeb3Connection();

    useEffect(() => {
        if (connected.success) {
            setIsOpen(false);
        }
    }, [connected.success]);

    const onOpenModal = (): void => {
        setIsOpen(true);
        trackEvent({
            event: 'e_ga_event',
            category: 'NFT',
            action: 'metamask connect entry',
            label: `connect - ${gaLabel}`,
        });
    };

    const onCloseModal = useCallback(() => {
        setIsOpen(false);
        trackEvent({
            event: 'e_ga_event',
            category: 'NFT',
            action: 'metamask connect exit',
            label: `closed by user - ${gaLabel}`,
        });
    }, [gaLabel]);

    return (
        <>
            {showAccountBalance ? (
                <ConnectedWalletStatus
                    user={viewer.user}
                    isMobile={isMobile}
                    showEditorial={!!viewer.nftMintBlog}
                    hideCreate={!!viewer.nftBenignNeglect}
                />
            ) : null}

            {!showAccountBalance && triedEagerConnect ? (
                <ConnectWalletButton onOpenModal={onOpenModal} isMobile={isMobile} />
            ) : null}

            <ModalContainer isOpen={isOpen} onClose={onCloseModal}>
                <ModalCloseButton onClick={onCloseModal} />
                <WithWalletConnectionEnsured
                    viewer={viewer}
                    persistWalletWithoutBidderRole
                    trackingLabel={gaLabel}
                    isMobile={isMobile}
                    authFlow={authModalLoader.constants.NFT_CONNECT_WALLET_FLOW}
                    onWalletConnected={() => setIsOpen(false)}
                >
                    {/* there's no need to show anything - parent unmounts this component as soon as conditions for wallet connection are met */}
                    {null}
                </WithWalletConnectionEnsured>
            </ModalContainer>
        </>
    );
};

type Props = {
    gaLabel?: string;
    isMobile: boolean;
};

export const EthereumWalletStatus: FC<Props> = ({ gaLabel = 'NFT', isMobile }) => {
    const environment = useRelayEnvironment();
    const [isClient, setIsClient] = useState(false);
    const [queryReference, loadQuery] =
        useQueryLoader<ethereumWalletStatusQueryType>(ethereumWalletStatusQuery);
    const [isRefetching, setIsRefetching] = useState(false);

    useEffect(() => {
        // only fetch on mount so that component doesn't suspend
        const userId = getBuyerId(document.cookie);
        loadQuery({
            userId: userId ?? '',
            fetchUser: !!userId,
        });
        setIsClient(true);
    }, [loadQuery]);

    const refetchQuery = useCallback(
        (userId: string) => {
            if (isRefetching) return;
            setIsRefetching(true);
            fetchQuery(environment, ethereumWalletStatusQuery, {
                userId,
                fetchUser: !!userId,
            }).subscribe({
                complete: () => {
                    setIsRefetching(false);
                    loadQuery({ userId, fetchUser: !!userId }, { fetchPolicy: 'store-or-network' });
                },
                error: () => {
                    setIsRefetching(false);
                },
            });
        },
        [environment, isRefetching, loadQuery]
    );

    if (!isClient || !queryReference) {
        return null;
    }

    return (
        <Suspense fallback={null}>
            <EthereumWalletComponent
                queryReference={queryReference}
                gaLabel={gaLabel}
                refetchQuery={refetchQuery}
                isMobile={isMobile}
            />
        </Suspense>
    );
};

export default EthereumWalletStatus;
